import { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, Bold, Italic, Link as LinkIcon, StickyNote, X } from "lucide-react";
import { toast } from "sonner";
import EmailChipsInput from "./EmailChipsInput";

const MAX_TOTAL_BYTES = 25 * 1024 * 1024; // Gmail attachment cap

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Composer({ threadId, mode, onSent, fromAddress, defaultTo }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]); // Array<File>
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const fileInputRef = useRef(null);

  const isNote = mode === "note";
  const canAttach = !isNote;

  // Seed the To row with the default recipient (the last incoming sender) the
  // first time the composer mounts for a thread. Note + when defaultTo
  // changes (threadId switches), reset state.
  useEffect(() => {
    if (isNote) return;
    setToList(defaultTo ? [defaultTo] : []);
    setCcList([]);
    setBccList([]);
    setShowCc(false);
    setShowBcc(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, defaultTo, isNote]);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const next = [...files, ...picked];
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      toast.error("Attachments exceed 25 MB (Gmail limit)");
      return;
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSend = async () => {
    if (!body.trim() && files.length === 0) return;
    if (!isNote && toList.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    try {
      if (isNote) {
        await api.post(`/threads/${threadId}/note`, { body_text: body });
        toast.success("Internal note added");
      } else if (files.length > 0) {
        const fd = new FormData();
        fd.append("body_text", body);
        fd.append("to", toList.join(", "));
        if (ccList.length) fd.append("cc", ccList.join(", "));
        if (bccList.length) fd.append("bcc", bccList.join(", "));
        for (const f of files) fd.append("files", f, f.name);
        await api.post(`/threads/${threadId}/reply`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Reply sent with ${files.length} attachment${files.length === 1 ? "" : "s"}`);
      } else {
        await api.post(`/threads/${threadId}/reply`, {
          body_text: body,
          to: toList,
          cc: ccList,
          bcc: bccList,
        });
        toast.success("Reply sent");
      }
      setBody("");
      setFiles([]);
      onSent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to send";
      if (typeof detail === "string" && /invalid_grant|token has been expired|token has been revoked/i.test(detail)) {
        toast.error("Gmail connection expired — reconnect from Admin → Inboxes, then resend.");
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`border-t shrink-0 ${isNote ? "border-amber-200 bg-amber-50/40" : "border-zinc-200 bg-white"}`}>
      <div className="px-3 md:px-5 py-2 md:py-3">
        <div className="flex items-center gap-2 mb-1 md:mb-2">
          <span className={`text-[10px] font-semibold tracking-[0.1em] uppercase ${isNote ? "text-amber-700" : "text-zinc-500"}`}>
            {isNote ? "Internal note — only your team will see this" : "Reply"}
          </span>
        </div>

        {/* Recipient chip rows — only for replies, not internal notes */}
        {!isNote && (
          <div className="space-y-1.5 mb-2" data-testid="composer-recipients">
            <div className="flex items-start gap-2">
              <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 pt-2.5 w-8 shrink-0">To</span>
              <div className="flex-1 min-w-0">
                <EmailChipsInput
                  value={toList}
                  onChange={setToList}
                  placeholder="name@example.com"
                  testIdPrefix="reply-to"
                />
              </div>
              {(!showCc || !showBcc) && (
                <div className="flex items-center gap-1 pt-2 shrink-0">
                  {!showCc && (
                    <button
                      type="button"
                      onClick={() => setShowCc(true)}
                      className="text-[11px] font-medium text-zinc-500 hover:text-[#0033CC] px-1.5"
                      data-testid="reply-show-cc"
                    >
                      Cc
                    </button>
                  )}
                  {!showBcc && (
                    <button
                      type="button"
                      onClick={() => setShowBcc(true)}
                      className="text-[11px] font-medium text-zinc-500 hover:text-[#0033CC] px-1.5"
                      data-testid="reply-show-bcc"
                    >
                      Bcc
                    </button>
                  )}
                </div>
              )}
            </div>
            {showCc && (
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 pt-2.5 w-8 shrink-0">Cc</span>
                <div className="flex-1 min-w-0">
                  <EmailChipsInput
                    value={ccList}
                    onChange={setCcList}
                    placeholder="cc@example.com"
                    testIdPrefix="reply-cc"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setShowCc(false); setCcList([]); }}
                  className="p-2 text-zinc-400 hover:text-red-600 shrink-0"
                  title="Remove Cc"
                  data-testid="reply-remove-cc"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            )}
            {showBcc && (
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 pt-2.5 w-8 shrink-0">Bcc</span>
                <div className="flex-1 min-w-0">
                  <EmailChipsInput
                    value={bccList}
                    onChange={setBccList}
                    placeholder="bcc@example.com"
                    testIdPrefix="reply-bcc"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setShowBcc(false); setBccList([]); }}
                  className="p-2 text-zinc-400 hover:text-red-600 shrink-0"
                  title="Remove Bcc"
                  data-testid="reply-remove-bcc"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            )}
          </div>
        )}

        <div className={`border rounded-md focus-within:ring-2 ${isNote ? "border-amber-300 focus-within:ring-amber-300 bg-white" : "border-zinc-200 focus-within:ring-[#0033CC] bg-white"}`}>
          {/* Toolbar — hidden on mobile (desktop only) */}
          <div className="hidden md:flex items-center gap-1 px-2 py-1.5 border-b border-zinc-100">
            <button type="button" className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors" title="Bold"><Bold className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            <button type="button" className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors" title="Italic"><Italic className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            <button type="button" className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors" title="Link"><LinkIcon className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            {canAttach && (
              <>
                <div className="w-px h-4 bg-zinc-200 mx-1" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors"
                  title="Attach files"
                  data-testid="composer-attach-button"
                >
                  <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={isNote ? "Leave a note for your teammates… use @name to mention" : "Type your reply…"}
            className="min-h-[72px] md:min-h-[120px] border-0 focus-visible:ring-0 resize-y text-base md:text-sm"
            data-testid={isNote ? "note-textarea" : "reply-textarea"}
          />
          {/* Attached files preview */}
          {files.length > 0 && (
            <div className="px-2 pb-2 pt-1 border-t border-zinc-100 flex flex-wrap gap-2" data-testid="composer-attachments">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="inline-flex items-center gap-1.5 max-w-full bg-zinc-100 border border-zinc-200 rounded-md pl-2 pr-1 py-1 text-xs"
                  data-testid={`composer-attachment-${i}`}
                >
                  <Paperclip className="w-3 h-3 text-zinc-500 shrink-0" strokeWidth={1.75} />
                  <span className="truncate max-w-[160px] md:max-w-[240px]">{f.name}</span>
                  <span className="text-zinc-500 shrink-0">· {fmtSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="ml-0.5 p-0.5 text-zinc-500 hover:text-red-600 hover:bg-white rounded"
                    title="Remove"
                    data-testid={`composer-remove-attachment-${i}`}
                  >
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Hidden file input shared by toolbar + mobile attach button */}
        {canAttach && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onPickFiles}
            className="hidden"
            data-testid="composer-file-input"
          />
        )}
        <div className="flex items-center justify-between mt-1.5 md:mt-2 gap-2">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {/* Mobile-only attach button (toolbar is hidden on mobile) */}
            {canAttach && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="md:hidden shrink-0 w-9 h-9 -ml-1 rounded-full text-zinc-600 hover:text-[#0033CC] hover:bg-zinc-100 active:bg-zinc-200 flex items-center justify-center"
                title="Attach files"
                data-testid="composer-attach-button-mobile"
              >
                <Paperclip className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
            <p className="text-[10px] md:text-[11px] text-zinc-500 truncate min-w-0">
              {isNote ? "Notes are private to your team." : `From ${fromAddress || "support@internalinbox.com"}`}
            </p>
          </div>
          <Button
            onClick={onSend}
            disabled={sending || (!body.trim() && files.length === 0) || (!isNote && toList.length === 0)}
            className={`shrink-0 h-9 md:h-8 px-4 md:px-3 ${isNote ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-[#0033CC] hover:bg-[#002299] text-white"}`}
            data-testid={isNote ? "send-note-button" : "send-reply-button"}
          >
            {isNote ? <StickyNote className="w-4 h-4 mr-1.5" strokeWidth={2} /> : <Send className="w-4 h-4 mr-1.5" strokeWidth={2} />}
            {sending ? "Sending…" : (isNote ? "Add note" : "Send")}
          </Button>
        </div>
      </div>
    </div>
  );
}
